import { useCallback } from 'react';
import { Form, getIn, useFormikContext } from 'formik';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { invariant } from '@e-socna/utils';
import type { EMPTY_STRING } from '@e-socna/empty';
import type { PrintStyleId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';
import type { DocumentTemplatePage_OneDocumentTemplate } from '../../pages/DocumentTemplate/hooks/useFindOneDocumentTemplateQuery.hook';

import { DocumentTypeField } from './DocumentTypeField';

interface Props {
  readonly onCancel?: () => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel: string;
  readonly title: string;
}

export const DocumentTemplateInfoFormFields = ({
  onCancel: handleCancel,
  setMustBlockAction,
  showCancelButton,
  submitLabel,
  title,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { printStyles } = useReferencesContext();

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<DocumentTemplatePage_OneDocumentTemplate>();

  invariant('code' in values.codingConfig, 'Unexpected codingConfig type');

  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  const handlePrintStyleIdChange = useCallback(
    (id: PrintStyleId | typeof EMPTY_STRING) => {
      void setFieldValue('printing.printSettings.printStyleId', id);
    },
    [setFieldValue],
  );

  return (
    <Form
      onSubmit={formikSubmit}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            {!!showCancelButton && (
              <Button onClick={handleCancel} size="large" variant="outlined">
                Annuler
              </Button>
            )}
          </Stack>
        }
        title={title}
      >
        <Grid container spacing={2}>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={
                !!(
                  getIn(errors, 'codingConfig.code') &&
                  getIn(touched, 'codingConfig.code')
                )
              }
              fullWidth
              helperText={getTouchedError(errors, touched, 'codingConfig.code')}
              id="document-template-coding-config-code"
              label="Code"
              name="codingConfig.code"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.codingConfig.code}
              variant="outlined"
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={!!(errors.label && touched.label)}
              fullWidth
              helperText={getTouchedError(errors, touched, 'label')}
              id="document-template-label"
              label="Libellé"
              name="label"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.label}
              variant="outlined"
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={!!(errors.document?.title && touched.document?.title)}
              fullWidth
              helperText={getTouchedError(errors, touched, 'document.title')}
              id="document-template-title"
              label="Titre"
              name="document.title"
              onBlur={handleBlur}
              onChange={handleChange}
              required={false}
              type="text"
              value={values.document.title}
              variant="outlined"
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <DocumentTypeField />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <Typography sx={{ mt: 2, mb: 2 }} variant="h5">
              Options d&apos;Impression
            </Typography>
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <FormControl
              error={
                !!(
                  errors.printing?.printSettings?.addHeaders &&
                  touched.printing?.printSettings?.addHeaders
                )
              }
              fullWidth
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={values.printing.printSettings.addHeaders}
                    id="document-print-settings-add-headers"
                    name="printing.printSettings.addHeaders"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                }
                label="Inclure entêtes de pages"
              />
              {!!(
                errors.printing?.printSettings?.addHeaders &&
                touched.printing?.printSettings?.addHeaders
              ) && (
                <FormHelperText>
                  {errors.printing.printSettings.addHeaders}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <FormControl
              error={
                !!(
                  errors.printing?.printSettings?.addFooters &&
                  touched.printing?.printSettings?.addFooters
                )
              }
              fullWidth
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={values.printing.printSettings.addFooters}
                    id="document-print-settings-add-footers"
                    name="printing.printSettings.addFooters"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                }
                label="Inclure pieds de pages"
              />
              {!!(
                errors.printing?.printSettings?.addFooters &&
                touched.printing?.printSettings?.addFooters
              ) && (
                <FormHelperText>
                  {errors.printing.printSettings.addFooters}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<PrintStyleId, IdOption<PrintStyleId>>
              error={
                !!(
                  errors.printing?.printSettings?.printStyleId &&
                  touched.printing?.printSettings?.printStyleId
                )
              }
              helperText={
                !!(
                  errors.printing?.printSettings?.printStyleId &&
                  touched.printing?.printSettings?.printStyleId
                ) &&
                (getIn(errors, 'printing.printSettings.printStyleId') as string)
              }
              id="document-print-style-id"
              label="Style d'impression"
              name="printing.printSettings.printStyleId"
              onBlur={handleBlur}
              onChange={handlePrintStyleIdChange}
              options={printStyles.map((printStyle) => ({
                id: printStyle._id,
                label: printStyle.label,
              }))}
              required
              value={values.printing.printSettings.printStyleId as PrintStyleId}
            />
          </Grid>
        </Grid>
      </FormLayout>
    </Form>
  );
};
